import * as React from 'react'
import { Loader2, Plus, Search, X } from 'lucide-react'
import { useFurniture } from '@/hooks/useFurniture'
import { useVenueInventory, useSetInventoryLine } from '@/hooks/useVenueInventory'
import { useToast } from '@/components/ui/toast'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { FormError } from '@/components/ui/form-error'
import { FurnitureTile } from '@/components/FurnitureTile'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { requestErrorMessage } from '@/helpers/apiClient'
import { eventService } from '@/services/EventService'
import { PlanImpactDialog } from '@/components/PlanImpactDialog'
import type { PlanImpactDto } from '@lib/dto/plan-impact.dto'
import type { VenueDto } from '@lib/dto/venue.dto'
import type { FurnitureItemDto } from '@lib/dto/furniture-item.dto'
import { furnitureClassLabel } from '@lib/enums/furniture-class.enum'
import type { VenueInventoryDto } from '@lib/dto/venue-inventory.dto'

/** One space-locked allocation of an item (some units pinned to a single space). */
interface Lock {
  key: string
  space: string
  qty: string
}
/** Per item: a movable (any-space) quantity plus zero or more space-locked ones. */
interface Row {
  movable: string
  locks: Lock[]
}

/** Parse a field to a non-negative integer (blank/invalid → 0, which removes the line). */
function toQty(value: string): number {
  const n = Number.parseInt(value, 10)
  return Number.isFinite(n) && n > 0 ? n : 0
}

/** Build editable row map from server inventory (stable lock keys per space). */
function rowsFromInventory(data: VenueInventoryDto): Record<string, Row> {
  const seed: Record<string, Row> = {}
  for (const entry of data.entries) {
    const row = (seed[entry.furnitureItemId] ??= { movable: '0', locks: [] })
    if (entry.venueSpaceId === null) row.movable = String(entry.quantity)
    else {
      row.locks.push({
        key: `${entry.furnitureItemId}:${entry.venueSpaceId}`,
        space: entry.venueSpaceId,
        qty: String(entry.quantity),
      })
    }
  }
  return seed
}

/**
 * Notion-style catalog picker: search the tenant catalog and add items that
 * aren’t already on this venue’s inventory list. Quiet list rows, no card chrome.
 */
function AddFromCatalogDialog({
  open,
  onOpenChange,
  candidates,
  busy,
  onAdd,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  candidates: FurnitureItemDto[]
  busy: boolean
  onAdd: (item: FurnitureItemDto) => void
}) {
  const [query, setQuery] = React.useState('')

  const q = query.trim().toLowerCase()
  const filtered = q
    ? candidates.filter(
        (item) =>
          item.name.toLowerCase().includes(q) ||
          furnitureClassLabel(item.class).toLowerCase().includes(q) ||
          item.class.toLowerCase().includes(q) ||
          (item.description ?? '').toLowerCase().includes(q),
      )
    : candidates

  function handleOpenChange(next: boolean): void {
    if (!next) setQuery('')
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md gap-3 p-4 sm:p-5">
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-base font-semibold">Add from catalog</DialogTitle>
          <DialogDescription className="text-sm text-ink-muted">
            Choose furniture to stock at this venue. You can set quantities after adding.
          </DialogDescription>
        </DialogHeader>

        <label className="relative block">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search catalog…"
            className="pl-8"
            aria-label="Search furniture catalog"
            autoFocus
          />
        </label>

        <div className="max-h-80 overflow-y-auto rounded-md border border-line">
          {candidates.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-ink-muted">
              Every catalog item is already on this venue.
            </p>
          ) : filtered.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-ink-muted">No matches for “{query.trim()}”.</p>
          ) : (
            <ul className="divide-y divide-line">
              {filtered.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onAdd(item)}
                    className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-surface-hover disabled:opacity-50"
                  >
                    <FurnitureTile primitives={item.primitives} imageUrl={item.imageUrl} size={40} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-ink">{item.name}</span>
                      <span className="block text-xs text-ink-muted">{furnitureClassLabel(item.class)}</span>
                    </span>
                    <Plus className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** The venue's counts, for someone who can see them but not change them. */
function InventoryCounts({
  venue,
  items,
  rowOf,
}: {
  venue: VenueDto
  items: FurnitureItemDto[]
  rowOf: (id: string) => Row
}) {
  if (items.length === 0) {
    return <p className="py-2 text-sm text-ink-muted">No furniture stocked here yet.</p>
  }
  const hasSpaces = venue.spaces.length > 0
  const spaceName = new Map(venue.spaces.map((s) => [s.id, s.name]))
  return (
    <div className="divide-y divide-line rounded-md border border-line">
      {items.map((item) => {
        const row = rowOf(item.id)
        return (
          <div key={item.id} className="px-3 py-3">
            <div className="flex items-center gap-3">
              <FurnitureTile primitives={item.primitives} imageUrl={item.imageUrl} size={36} />
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{item.name}</span>
              <span className="flex items-center gap-1.5 text-xs text-ink-muted">
                {hasSpaces ? 'Any space' : 'Quantity'}
                <span className="font-mono tabular-nums text-ink">{row.movable}</span>
              </span>
            </div>
            {row.locks.length > 0 ? (
              <div className="mt-2 space-y-1.5 pl-1">
                {row.locks.map((lock) => (
                  <div key={lock.key} className="flex items-center gap-2 text-xs text-ink-muted">
                    <span className="min-w-0 flex-1 truncate">Locked to {spaceName.get(lock.space) ?? 'a space'}</span>
                    <span className="font-mono tabular-nums text-ink">{lock.qty}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

/**
 * Edit a venue's furniture inventory in place. Each item can have a **movable**
 * quantity (usable in any space) plus quantities **locked to a specific space**.
 * Fields commit on blur. Use **Add from catalog** to stock catalog pieces that
 * aren’t on this venue yet.
 */
export function VenueInventoryEditor({
  venue,
  canEdit,
}: {
  venue: VenueDto
  /** Edit adds changing counts, locking units to a space, and stocking more pieces; without it the counts are read. */
  canEdit: boolean
}) {
  // This venue's catalog. Unscoped, "Add from catalog" offered other venues'
  // pieces, and stocking one wrote an inventory line at this venue pointing at
  // another venue's item — the cross-venue reference the split removed.
  const catalog = useFurniture(undefined, venue.id)
  const inventory = useVenueInventory(venue.id)
  const setLine = useSetInventoryLine(venue.id)
  const { toast } = useToast()
  /** Saves for one piece, in the order they were asked for. See `chain`. */
  const tails = React.useRef(new Map<string, Promise<void>>())
  const entriesRef = React.useRef(inventory.data?.entries ?? [])
  // The count a blur or a room move compares against. Kept off the render path;
  // a save reads it from a handler, after this has caught up with the server.
  React.useLayoutEffect(() => {
    if (inventory.data) entriesRef.current = inventory.data.entries
  }, [inventory.data])

  // Local overrides for in-progress edits; server snapshot is the baseline.
  const [overrides, setOverrides] = React.useState<Record<string, Row>>({})
  const [pickerOpen, setPickerOpen] = React.useState(false)
  const [addingId, setAddingId] = React.useState<string | null>(null)
  /**
   * A lower count waiting on its confirmation: which plans it would leave short,
   * and the write to make if the person goes ahead.
   */
  const [pendingStock, setPendingStock] = React.useState<{
    itemId: string
    itemName: string
    quantity: number
    impact: PlanImpactDto | null
    error: string | null
    run: () => void
    /** Lets the queued save finish when the dialog is dismissed without saving. */
    cancel: () => void
  } | null>(null)

  const serverRows = React.useMemo(
    () => (inventory.data ? rowsFromInventory(inventory.data) : {}),
    [inventory.data],
  )

  if (catalog.isPending || inventory.isPending) {
    return (
      <p className="flex items-center gap-2 py-2 text-sm text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading inventory…
      </p>
    )
  }
  if (catalog.isError || inventory.isError) {
    return <FormError className="py-2">Failed to load inventory.</FormError>
  }

  const catalogItems = catalog.data.items
  const entries = inventory.data.entries
  const hasSpaces = venue.spaces.length > 0

  // Rows to show: stocked, available at this venue, or locally added this session.
  const listedIds = new Set<string>()
  for (const e of entries) listedIds.add(e.furnitureItemId)
  for (const item of catalogItems) {
    if (item.venueId === venue.id) listedIds.add(item.id)
  }
  for (const id of Object.keys(overrides)) listedIds.add(id)

  const listedItems = catalogItems
    .filter((item) => listedIds.has(item.id))
    .sort((a, b) => a.name.localeCompare(b.name))

  const addableItems = catalogItems
    .filter((item) => !listedIds.has(item.id))
    .sort((a, b) => a.name.localeCompare(b.name))

  const rowOf = (id: string): Row => overrides[id] ?? serverRows[id] ?? { movable: '0', locks: [] }

  if (!canEdit) {
    return <InventoryCounts venue={venue} items={listedItems} rowOf={rowOf} />
  }

  function update(id: string, next: Partial<Row>): void {
    setOverrides((prev) => ({ ...prev, [id]: { ...rowOf(id), ...next } }))
  }

  function storedQty(itemId: string, spaceId: string | null): number {
    return entriesRef.current.find((e) => e.furnitureItemId === itemId && e.venueSpaceId === spaceId)?.quantity ?? 0
  }

  /**
   * One save at a time for a piece, in the order they were asked for.
   *
   * Changing rooms blurs the quantity field first, and that save used to race
   * the move. If it landed last, the old room kept the stock and the new room
   * had a copy.
   */
  function chain(itemId: string, job: () => Promise<void>): Promise<void> {
    const prev = tails.current.get(itemId) ?? Promise.resolve()
    const done = prev.then(job, job)
    tails.current.set(
      itemId,
      done.then(
        () => undefined,
        () => undefined,
      ),
    )
    return done
  }

  /**
   * Save a count — asking first when a lower one would leave plans short.
   *
   * Stock went down on blur, and to nothing when a field was cleared, with no
   * word to anyone: the plans that placed more than the new count found out
   * when somebody opened them. Now a lower count checks which plans still being
   * arranged it would leave short, and says so, naming them, before it saves.
   *
   * The whole check is queued on the piece, so a room change asked in the same
   * gesture waits until this save has landed.
   */
  function commit(itemId: string, spaceId: string | null, quantity: number, then?: () => Promise<void>): void {
    const itemName = catalogItems.find((i) => i.id === itemId)?.name ?? 'this piece'
    void chain(itemId, () => new Promise<void>((resolve) => {
      let settled = false
      let claimed = false
      const finishWait = (): void => {
        if (settled) return
        settled = true
        resolve()
      }
      const stored = storedQty(itemId, spaceId)
      const persist = async (): Promise<void> => {
        try {
          if (quantity !== stored) {
            await setLine.mutateAsync({ furnitureItemId: itemId, venueSpaceId: spaceId, quantity })
          }
          if (then) await then()
        } catch (e) {
          toast(requestErrorMessage(e), { variant: 'error' })
        } finally {
          finishWait()
        }
      }
      if (quantity >= stored) {
        void persist()
        return
      }
      void eventService
        .stockImpact({ venueId: venue.id, furnitureItemId: itemId, venueSpaceId: spaceId, quantity })
        .then((impact) => {
          if (settled) return
          if (impact.plans.length === 0) {
            void persist()
            return
          }
          setPendingStock({
            itemId,
            itemName,
            quantity,
            impact,
            error: null,
            run: () => {
              claimed = true
              void persist()
            },
            cancel: () => {
              if (claimed) return
              finishWait()
            },
          })
        })
        .catch((e: Error) => {
          if (settled) return
          setPendingStock({
            itemId,
            itemName,
            quantity,
            impact: null,
            error: requestErrorMessage(e),
            run: () => {
              claimed = true
              void persist()
            },
            cancel: () => {
              if (claimed) return
              finishWait()
            },
          })
        })
    }))
  }

  function commitMovable(id: string): void {
    commit(id, null, toQty(rowOf(id).movable))
  }
  function commitLock(id: string, lock: Lock): void {
    if (lock.space) commit(id, lock.space, toQty(lock.qty))
  }
  function setLockField(id: string, key: string, next: Partial<Lock>): void {
    update(id, { locks: rowOf(id).locks.map((l) => (l.key === key ? { ...l, ...next } : l)) })
  }
  function changeLockSpace(id: string, lock: Lock, nextSpace: string): void {
    setLockField(id, lock.key, { space: nextSpace })
    if (nextSpace === lock.space) return
    const qty = toQty(lock.qty)
    // The old room first, confirmed if that leaves plans short, and only then the
    // new one. The follow-up write stays inside this queued save: starting its
    // own would wait behind the save it is already part of.
    commit(id, lock.space, 0, async () => {
      if (qty <= 0) return
      await setLine.mutateAsync({ furnitureItemId: id, venueSpaceId: nextSpace, quantity: qty })
    })
  }
  function addLock(id: string): void {
    const used = new Set(rowOf(id).locks.map((l) => l.space))
    const free = venue.spaces.find((s) => !used.has(s.id))
    if (!free) return
    update(id, {
      locks: [...rowOf(id).locks, { key: `${id}:${free.id}:new`, space: free.id, qty: '0' }],
    })
  }
  function removeLock(id: string, lock: Lock): void {
    update(id, { locks: rowOf(id).locks.filter((l) => l.key !== lock.key) })
    commit(id, lock.space, 0)
  }

  /** Stock one catalog item here: inventory line + catalog availability when allowed. */
  function addFromCatalog(item: FurnitureItemDto): void {
    setAddingId(item.id)
    setOverrides((prev) => ({
      ...prev,
      [item.id]: prev[item.id] ?? { movable: '1', locks: [] },
    }))

    const finish = (): void => {
      setLine.mutate(
        { furnitureItemId: item.id, venueSpaceId: null, quantity: 1 },
        {
          onSuccess: () => {
            setAddingId(null)
            setPickerOpen(false)
          },
          onError: (e) => {
            setAddingId(null)
            toast(requestErrorMessage(e), { variant: 'error' })
          },
        },
      )
    }

    // A piece offered here used to be widened to include this venue as it was
    // stocked. There is nothing to widen now: it belongs to this venue or it is
    // not on this list.
    finish()
  }

  const saving = setLine.isPending

  return (
    <div className="space-y-3">
      <div className="flex h-4 items-center justify-end text-xs text-ink-muted" aria-live="polite">
        {saving ? (
          <span className="flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" /> Saving…
          </span>
        ) : null}
      </div>

      {listedItems.length === 0 ? (
        <div className="rounded-md border border-dashed border-line px-4 py-8 text-center">
          <p className="text-sm text-ink-muted">
            {catalogItems.length === 0
              ? 'No catalog items yet — create furniture in the catalog first.'
              : 'No furniture stocked here yet.'}
          </p>
          {catalogItems.length > 0 ? (
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="mt-3 inline-flex items-center gap-1 text-sm text-ink-muted transition-colors hover:text-ink"
            >
              <Plus className="h-4 w-4" /> Add from catalog
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <div className="divide-y divide-line rounded-md border border-line">
            {listedItems.map((item) => {
              const row = rowOf(item.id)
              const usedSpaces = new Set(row.locks.map((l) => l.space))
              const canLockMore = venue.spaces.some((s) => !usedSpaces.has(s.id))
              return (
                <div key={item.id} className="group px-3 py-3">
                  <div className="flex items-center gap-3">
                    <FurnitureTile primitives={item.primitives} imageUrl={item.imageUrl} size={36} />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">{item.name}</span>
                    <label className="flex items-center gap-1.5 text-xs text-ink-muted">
                      {hasSpaces ? 'Any space' : 'Quantity'}
                      <Input
                        size="sm"
                        type="number"
                        min={0}
                        value={row.movable}
                        onChange={(e) => update(item.id, { movable: e.target.value })}
                        onBlur={() => commitMovable(item.id)}
                        className="w-16 text-right"
                        aria-label={`Movable quantity of ${item.name}`}
                      />
                    </label>
                  </div>
                  {hasSpaces ? (
                    <div className="mt-2 space-y-1.5 pl-1">
                      {row.locks.map((lock) => (
                        <div key={lock.key} className="flex items-center gap-2">
                          <span className="text-xs text-ink-muted">Locked to</span>
                          <div className="flex-1">
                            <Select
                              size="sm"
                              value={lock.space}
                              onChange={(e) => changeLockSpace(item.id, lock, e.target.value)}
                              aria-label={`Space for locked ${item.name}`}
                            >
                              {venue.spaces
                                .filter((s) => s.id === lock.space || !usedSpaces.has(s.id))
                                .map((s) => (
                                  <option key={s.id} value={s.id}>
                                    {s.name}
                                  </option>
                                ))}
                            </Select>
                          </div>
                          <Input
                            size="sm"
                            type="number"
                            min={0}
                            value={lock.qty}
                            onChange={(e) => setLockField(item.id, lock.key, { qty: e.target.value })}
                            onBlur={() => commitLock(item.id, lock)}
                            className="w-16 text-right"
                            aria-label={`Quantity of ${item.name} locked to a space`}
                          />
                          <button
                            type="button"
                            aria-label="Remove space lock"
                            onClick={() => removeLock(item.id, lock)}
                            className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-ink-muted opacity-0 transition-opacity hover:bg-surface-hover hover:text-ink group-hover:opacity-100 focus-visible:opacity-100"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      {canLockMore ? (
                        <button
                          type="button"
                          onClick={() => addLock(item.id)}
                          className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-xs text-ink-muted transition-colors hover:text-ink"
                        >
                          <Plus className="h-3.5 w-3.5" /> Lock some to a space
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>

          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="inline-flex items-center gap-1 rounded-md px-1 py-1 text-sm text-ink-muted transition-colors hover:text-ink"
          >
            <Plus className="h-4 w-4" /> Add from catalog
          </button>
        </>
      )}

      <PlanImpactDialog
        open={pendingStock !== null}
        onOpenChange={(open) => {
          if (open || !pendingStock) return
          // Put the field back to the stored count: nothing was saved.
          // `cancel` lets the next save for this piece run; confirming already
          // took the queue, and a second release would start it too early.
          pendingStock.cancel()
          const id = pendingStock.itemId
          setOverrides((prev) => {
            const next = { ...prev }
            delete next[id]
            return next
          })
          setPendingStock(null)
        }}
        title={`Lower ${pendingStock?.itemName ?? 'this piece'} to ${String(pendingStock?.quantity ?? 0)}?`}
        intro={(n) =>
          `${String(n)} ${n === 1 ? 'plan still being arranged' : 'plans still being arranged'} would be short. They keep their pieces; the planner sees the shortfall.`
        }
        piecesLabel={(p) => `${String(p)} short`}
        settledNote={(c) =>
          `${String(c)} confirmed or finished ${c === 1 ? 'plan keeps' : 'plans keep'} the stock ${c === 1 ? 'it was' : 'they were'} settled with.`
        }
        impact={pendingStock?.impact ?? null}
        loading={false}
        error={pendingStock?.error ?? null}
        confirmLabel="Lower stock"
        destructive
        onConfirm={() => {
          pendingStock?.run()
          setPendingStock(null)
        }}
      />

      <AddFromCatalogDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        candidates={addableItems}
        busy={addingId !== null || saving}
        onAdd={addFromCatalog}
      />
    </div>
  )
}
